import { isOutOfView, isReachable, resolveAnchor } from './anchors';
import { installBackdropGuard } from './backdropGuard';
import { restoreHostChrome, suppressHostChrome } from './hostChrome';
import { fillField, isFilled } from '../tasks/formFill';
import type { CalloutSide, TaskFlow, TaskStep } from '../tasks/types';

const TICK_MS = 140;

const RESOLVE_TIMEOUT_MS = 5000;

const PRE_ACTION_HOLD_MS = 550;

const REWIND_TIMEOUT_MS = 3000;

const ACTIVE_CLASS = 'tour-task-active';

const NUDGE_INTERVAL_MS = 700;

const USER_SCROLL_GRACE_MS = 1500;

const SELF_SCROLL_WINDOW_MS = 300;

export function shouldCorrectView({
  now,
  lastNudgeAt,
  userScrolledAt,
  force = false,
}: {
  now: number;
  lastNudgeAt: number;
  userScrolledAt: number;
  force?: boolean;
}): boolean {
  if (force) return true;
  if (now - lastNudgeAt < NUDGE_INTERVAL_MS) return false;
  if (now - userScrolledAt < USER_SCROLL_GRACE_MS) return false;
  return true;
}

const TRACE = Boolean(import.meta.env?.DEV);

function trace(...args: unknown[]): void {
  if (TRACE) console.info('[tour-task]', ...args);
}

function describe(el: Element | null): string {
  if (!el) return 'null';
  const id = el.id ? `#${el.id}` : '';
  const testid = el.getAttribute('data-testid');
  const rect = el.getBoundingClientRect();
  return (
    `${el.tagName.toLowerCase()}${id}` +
    `${testid ? `[data-testid=${testid}]` : ''} ` +
    `${Math.round(rect.width)}x${Math.round(rect.height)}@${Math.round(rect.left)},${Math.round(rect.top)}`
  );
}

export interface TaskTargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export type TaskStepState = 'complete' | 'active' | 'upcoming' | 'skipped';

export interface TaskStepView {
  id: string;
  label: string;
  state: TaskStepState;
}

export interface TaskRunState {
  running: boolean;
  taskId: string | null;
  title: string;
  summary: string;
  doneTitle: string;
  doneDescription: string;
  whenDone: 'panel' | 'resume' | 'close';
  preparing: boolean;
  preparingLabel: string;
  index: number;
  total: number;
  steps: TaskStepView[];
  step: {
    id: string;
    title: string;
    description: string;
    side: CalloutSide;
    waiting: boolean;
    waitingHint: string | null;
    satisfied: boolean;
    auto: boolean;
    nextLabel: string | null;
    canSkipSegment: boolean;
    lost: boolean;
    awaiting: boolean;
    spotlight: boolean;
  } | null;
  target: TaskTargetRect | null;
  avoid: TaskTargetRect | null;
  finished: boolean;
  skippedCount: number;
  exited: boolean;
}

function preparingLabelFor(task: TaskFlow): string {
  return task.preparingLabel ?? 'Opening the module…';
}

export const IDLE_TASK_STATE: TaskRunState = {
  running: false,
  taskId: null,
  title: '',
  summary: '',
  doneTitle: '',
  doneDescription: '',
  whenDone: 'panel',
  preparing: false,
  preparingLabel: '',
  index: -1,
  total: 0,
  steps: [],
  step: null,
  target: null,
  avoid: null,
  finished: false,
  skippedCount: 0,
  exited: false,
};

export interface TaskEngineOptions {
  onState(state: TaskRunState): void;
  onFinish?(taskId: string): void;
}

export class TaskEngine {
  private options: TaskEngineOptions;
  private task: TaskFlow | null = null;
  private index = -1;
  private timer: number | null = null;
  private suppressed: HTMLElement[] = [];

  private releaseBackdrop: (() => void) | null = null;

  private done = new Set<string>();
  private skipped = new Set<string>();

  private entering = false;

  private awaitingTarget = false;

  private lastNudgeAt = 0;

  private userScrolledAt = 0;

  private selfScrollUntil = 0;

  private advanceQueued = false;

  private queuedMove: 'back' | 'skip' | null = null;

  private backstopIndex: number | null = null;

  private lookingSince = 0;

  private occluded = false;

  private el: HTMLElement | null = null;

  private signature = '';

  private onViewportChange: (() => void) | null = null;

  private filled = new Set<string>();

  private finished = false;
  private exited = false;

  constructor(options: TaskEngineOptions) {
    this.options = options;
  }

  get running(): boolean {
    return this.task !== null;
  }

  start(task: TaskFlow): boolean {
    if (this.task) this.stop();

    this.task = task;
    this.done = new Set();
    this.skipped = new Set();
    this.filled = new Set();
    this.finished = false;
    this.exited = false;
    this.awaitingTarget = false;
    this.lastNudgeAt = 0;
    this.userScrolledAt = 0;
    this.selfScrollUntil = 0;
    this.signature = '';
    this.suppressed = suppressHostChrome();
    this.releaseBackdrop = installBackdropGuard(() => this.el);
    document.body.classList.add(ACTIVE_CLASS);

    this.index = -1;
    this.publishPreparing(task);

    void this.beginFlow(task);
    this.timer = window.setInterval(() => this.tick(), TICK_MS);

    this.onViewportChange = (event?: Event) => {
      if (event?.type === 'scroll' && Date.now() >= this.selfScrollUntil) {
        this.userScrolledAt = Date.now();
      }
      if (!this.task || this.entering) return;
      this.resolve();
      this.publish(false);
    };
    document.addEventListener('scroll', this.onViewportChange, { capture: true, passive: true });
    window.addEventListener('resize', this.onViewportChange, { passive: true });

    return true;
  }

  next(): void {
    if (!this.task || this.finished) return;
    if (this.entering) {
      this.advanceQueued = true;
      trace('  Next pressed while the step was still setting up — queued.');
      return;
    }
    const step = this.task.steps[this.index];
    if (!step) return;
    this.backstopIndex = null;
    void this.advanceFromNext(this.task, this.index, step);
  }

  private async advanceFromNext(task: TaskFlow, index: number, step: TaskStep): Promise<void> {
    if (step.onNext) {
      try {
        await step.onNext();
      } catch (error) {
        console.warn(`[tour-guide] task "${task.id}" step "${step.id}": onNext failed`, error);
      }
      if (this.task !== task || this.index !== index) return;
    }
    if (this.isSatisfied(step)) this.done.add(step.id);
    else this.skipped.add(step.id);
    void this.enter(index + 1);
  }

  previous(): boolean {
    if (!this.task) return false;

    if (this.entering) {
      this.queuedMove = 'back';
      trace('  Back pressed while the step was still setting up — queued.');
      return true;
    }

    let target = this.index - 1;
    while (target >= 0) {
      const step = this.task.steps[target];
      if (step.advance === 'auto' && this.isSatisfied(step)) {
        target -= 1;
        continue;
      }
      const hit = resolveAnchor(step.anchor);
      if (!hit || !isReachable(hit.element)) {
        target -= 1;
        continue;
      }
      break;
    }

    if (target < 0) {
      target = this.index - 1;
      while (target >= 0) {
        const step = this.task.steps[target];
        if (step.advance === 'auto' && this.isSatisfied(step)) {
          target -= 1;
          continue;
        }
        break;
      }
    }

    if (target < 0) return false;

    this.task.steps.slice(target).forEach((s) => {
      this.done.delete(s.id);
      this.skipped.delete(s.id);
    });
    this.finished = false;
    this.backstopIndex = this.index;
    void this.rewindThenEnter(this.task, this.index, target);
    return true;
  }

  private async rewindThenEnter(task: TaskFlow, from: number, target: number): Promise<void> {
    this.entering = true;

    for (let i = from; i >= target; i -= 1) {
      const step = task.steps[i];
      if (!step?.rewind) continue;
      trace(`  rewinding "${step.id}" on the way back to step ${target + 1}.`);
      try {
        await this.bounded(step.rewind(), REWIND_TIMEOUT_MS);
      } catch (error) {
        console.warn(`[tour-guide] task "${task.id}" step "${step.id}": rewind failed`, error);
      }
      if (this.task !== task) return;
    }

    if (this.task !== task) return;
    this.entering = false;

    const pressedDuringRewind = this.queuedMove;
    void this.enter(target);
    if (pressedDuringRewind) this.queuedMove = pressedDuringRewind;
  }

  private bounded(work: void | Promise<void>, ms: number): Promise<void> {
    return Promise.race([
      Promise.resolve(work),
      new Promise<void>((resolve) => window.setTimeout(resolve, ms)),
    ]);
  }

  skipSegment(): boolean {
    if (!this.task || this.finished) return true;
    const step = this.task.steps[this.index];
    const target = step?.skipTo;
    if (!target) return true;

    const landing = target === 'end'
      ? -1
      : this.task.steps.findIndex((s, i) => i > this.index && s.id === target);

    if (landing < 0) {
      if (target !== 'end' && import.meta.env.DEV) {
        console.warn(
          `[tour-guide] task "${this.task.id}" step "${step?.id}": skipTo "${target}" ` +
            'is not a later step in this flow — ending the flow instead.',
        );
      }
      this.task.steps.slice(this.index).forEach((s) => this.skipped.add(s.id));
      this.publish(true);
      return false;
    }

    this.task.steps.slice(this.index, landing).forEach((s) => this.skipped.add(s.id));
    this.backstopIndex = null;
    void this.enter(landing);
    return true;
  }

  skip(): void {
    if (!this.task || this.finished) return;
    if (this.entering) {
      this.queuedMove = 'skip';
      trace('  Skip pressed while the step was still setting up — queued.');
      return;
    }
    const step = this.task.steps[this.index];
    if (step) this.skipped.add(step.id);
    this.backstopIndex = null;
    void this.enter(this.index + 1);
  }

  stop(): void {
    if (!this.task) return;
    if (!this.finished) this.exited = true;
    this.clearTimer();
    restoreHostChrome(this.suppressed);
    this.suppressed = [];
    this.releaseBackdrop?.();
    this.releaseBackdrop = null;
    document.body.classList.remove(ACTIVE_CLASS);
    this.task = null;
    this.index = -1;
    this.el = null;
    this.entering = false;
    this.awaitingTarget = false;
    this.advanceQueued = false;
    this.queuedMove = null;
    this.backstopIndex = null;
    this.signature = '';
    this.options.onState({ ...IDLE_TASK_STATE, exited: this.exited });
  }

  destroy(): void {
    this.stop();
  }

  private async beginFlow(task: TaskFlow): Promise<void> {
    try {
      await task.beforeStart?.();
    } catch (error) {
      console.warn(`[tour-guide] task "${task.id}": beforeStart failed`, error);
    }
    if (this.task !== task) return;
    void this.enter(0);
  }

  private async enter(index: number): Promise<void> {
    const task = this.task;
    if (!task || this.entering) return;

    if (index >= task.steps.length) {
      this.index = task.steps.length;
      this.finished = true;
      this.el = null;
      this.publish(true);
      this.options.onFinish?.(task.id);
      return;
    }

    this.entering = true;
    this.advanceQueued = false;
    this.queuedMove = null;
    try {
      this.index = index;
      this.el = null;
      this.awaitingTarget = !resolveAnchor(task.steps[index].anchor);
      this.occluded = false;
      this.lookingSince = Date.now();
      const step = task.steps[index];
      trace(
        `step ${index + 1}/${task.steps.length} "${step.id}"`,
        `advance=${step.advance ?? 'manual'}`,
        `satisfiedOnEntry=${this.isSatisfied(step)}`,
        `anchorResolvesOnEntry=${describe(resolveAnchor(step.anchor)?.element ?? null)}`,
      );

      this.publish(true);

      const alreadySatisfied = this.isSatisfied(step);
      if (step.prepare && (!alreadySatisfied || step.forceHighlight)) {
        await this.waitForTarget(step, RESOLVE_TIMEOUT_MS);
        if (this.task !== task || this.index !== index) return;
        if (this.el) {
          await this.sleep(PRE_ACTION_HOLD_MS);
          if (this.task !== task || this.index !== index) return;
        } else {
          console.warn(
            `[tour-guide] task "${task.id}" step "${step.id}": no target confirmed within ` +
              `${RESOLVE_TIMEOUT_MS}ms (anchor resolved but ready() never agreed, or nothing ` +
              `resolved at all) — proceeding to prepare() without a highlight.`,
          );
        }
      } else {
        this.resolve();
        this.publish(true);
      }

      try {
        await step.prepare?.();
      } catch (error) {
        console.warn(`[tour-guide] task "${task.id}" step "${step.id}": prepare failed`, error);
      }

      if (step.prepare) this.awaitingTarget = false;

      trace(
        `  after prepare "${step.id}"`,
        `satisfied=${this.isSatisfied(step)}`,
        `anchor=${describe(resolveAnchor(step.anchor)?.element ?? null)}`,
      );

      this.lookingSince = Date.now();

      if (this.task === task) this.applyFill(step);

      if (this.task === task) this.publish(true);

      if (this.task === task && step.advance === 'auto' && this.isSatisfied(step)) {
        this.done.add(step.id);
        this.entering = false;
        await this.enter(index + 1);
        return;
      }

      if (this.task === task) this.resolve();
    } finally {
      this.entering = false;
    }
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }

  private async waitForTarget(step: TaskStep, timeoutMs: number): Promise<void> {
    const startedAt = Date.now();
    while (this.task) {
      this.resolve();
      if (this.el && (step.ready?.() ?? true)) {
        this.publish(true);
        return;
      }
      if (Date.now() - startedAt >= timeoutMs) return;
      await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
    }
  }

  private tick(): void {
    const task = this.task;
    if (!task || this.entering || this.finished) return;

    if (this.queuedMove) {
      const move = this.queuedMove;
      this.queuedMove = null;
      if (move === 'back') this.previous();
      else this.skip();
      return;
    }

    if (this.advanceQueued) {
      this.advanceQueued = false;
      this.next();
      return;
    }

    const step = task.steps[this.index];
    if (!step) return;

    this.resolve();
    this.applyFill(step);

    if (step.advance === 'auto' && this.isSatisfied(step)) {
      if (this.blockedByBackstop(this.index + 1)) return;
      this.done.add(step.id);
      void this.enter(this.index + 1);
      return;
    }

    if (!this.el && !this.occluded && Date.now() - this.lookingSince > RESOLVE_TIMEOUT_MS) {
      const optional = step.optional ?? true;
      if (optional && !this.blockedByBackstop(this.index + 1)) {
        trace(
          `  SKIPPING "${step.id}" — its anchor matched nothing for ${RESOLVE_TIMEOUT_MS}ms.`,
          'This is the path that makes a filled field look unfilled: the step is',
          'dropped and never pointed at. Check the selector against the live DOM.',
        );
        this.skipped.add(step.id);
        void this.enter(this.index + 1);
        return;
      }
    }

    this.publish(false);
  }

  private blockedByBackstop(next: number): boolean {
    return this.backstopIndex !== null && next >= this.backstopIndex;
  }

  private resolve(): void {
    const step = this.task?.steps[this.index];
    if (!step) return;

    const hit = resolveAnchor(step.anchor);
    const found = hit && isReachable(hit.element) ? hit.element : null;
    this.occluded = Boolean(hit) && !found;

    if (found && found !== this.el) {
      this.el = found;
      this.awaitingTarget = false;
      this.bringIntoView(found, { force: true });
      return;
    }

    if (found) {
      if (isOutOfView(found)) this.bringIntoView(found);
      return;
    }

    if (this.occluded) {
      this.el = null;
      return;
    }

    if (this.el && !this.el.isConnected) this.el = null;
  }

  private bringIntoView(el: HTMLElement, { force = false }: { force?: boolean } = {}): void {
    const now = Date.now();
    const allowed = shouldCorrectView({
      now,
      lastNudgeAt: this.lastNudgeAt,
      userScrolledAt: this.userScrolledAt,
      force,
    });
    if (!allowed) return;

    this.lastNudgeAt = now;
    this.selfScrollUntil = now + SELF_SCROLL_WINDOW_MS;
    try {
      el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'auto' });
    } catch {
      el.scrollIntoView();
    }
  }

  private applyFill(step: TaskStep): void {
    if (!step.fill || step.fill.length === 0 || this.filled.has(step.id)) return;

    let allPresent = true;
    for (const [selector, value] of step.fill) {
      if (isFilled(selector)) continue;
      const wrote = fillField(selector, value, { onlyIfEmpty: true });
      if (!isFilled(selector)) {
        allPresent = false;
        if (!wrote) {
          trace(`  fill pending "${step.id}"`, selector, describe(resolveAnchor({ selector })?.element ?? null));
        }
      }
    }

    if (allPresent) {
      this.filled.add(step.id);
      trace(`  fill complete "${step.id}"`);
    }
  }

  private isSatisfied(step: TaskStep): boolean {
    if (!step.satisfied) return true;
    try {
      return step.satisfied();
    } catch (error) {
      console.warn(`[tour-guide] task step "${step.id}": satisfied() threw`, error);
      return false;
    }
  }

  private stepViews(task: TaskFlow): TaskStepView[] {
    return task.steps.map((step, i) => {
      if (this.skipped.has(step.id)) return { id: step.id, label: step.label, state: 'skipped' };
      if (this.done.has(step.id)) return { id: step.id, label: step.label, state: 'complete' };
      if (i === this.index && !this.finished) {
        return { id: step.id, label: step.label, state: 'active' };
      }
      return { id: step.id, label: step.label, state: 'upcoming' };
    });
  }

  private rect(): TaskTargetRect | null {
    if (!this.el) return null;
    const r = this.el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return null;
    return { top: r.top, left: r.left, width: r.width, height: r.height };
  }

  private avoidRect(): TaskTargetRect | null {
    const host = this.el?.closest('[role="dialog"], [role="alertdialog"]') as HTMLElement | null;
    if (!host) return null;
    const r = host.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return null;
    return { top: r.top, left: r.left, width: r.width, height: r.height };
  }

  private publishPreparing(task: TaskFlow): void {
    this.signature = '';
    this.options.onState({
      running: true,
      taskId: task.id,
      title: task.title,
      summary: task.summary,
      doneTitle: task.doneTitle,
      doneDescription: task.doneDescription,
      whenDone: task.whenDone ?? 'panel',
      preparing: true,
      preparingLabel: preparingLabelFor(task),
      index: -1,
      total: task.steps.length,
      steps: task.steps.map((step) => ({ id: step.id, label: step.label, state: 'upcoming' })),
      step: null,
      target: null,
      avoid: null,
      finished: false,
      skippedCount: 0,
      exited: false,
    });
  }

  private publish(force: boolean): void {
    const task = this.task;
    if (!task) return;

    const step = task.steps[this.index] ?? null;
    const satisfied = step ? this.isSatisfied(step) : true;
    const auto = step?.advance === 'auto';
    const target = this.rect();
    const avoid = this.avoidRect();
    const lost = !this.el && Date.now() - this.lookingSince > RESOLVE_TIMEOUT_MS;

    const state: TaskRunState = {
      running: true,
      taskId: task.id,
      title: task.title,
      summary: task.summary,
      doneTitle: task.doneTitle,
      doneDescription: task.doneDescription,
      whenDone: task.whenDone ?? 'panel',
      preparing: false,
      preparingLabel: preparingLabelFor(task),
      index: this.index,
      total: task.steps.length,
      steps: this.stepViews(task),
      step:
        step && !this.finished
          ? {
              id: step.id,
              title: step.title,
              description: step.description,
              side: step.side ?? 'right',
              waiting: auto && !satisfied,
              waitingHint: step.waitingHint ?? null,
              satisfied,
              auto,
              nextLabel: step.nextLabel ?? null,
              canSkipSegment: Boolean(step.skipTo) && step.skipTo !== 'end',
              lost,
              awaiting: this.awaitingTarget && !this.el && !lost,
              spotlight: Boolean(step.spotlight),
            }
          : null,
      target,
      avoid,
      finished: this.finished,
      skippedCount: this.skipped.size,
      exited: false,
    };

    const signature = [
      state.index,
      state.finished,
      satisfied,
      lost,
      target ? Math.round(target.top) : 'x',
      target ? Math.round(target.left) : 'x',
      target ? Math.round(target.width) : 'x',
      target ? Math.round(target.height) : 'x',
      this.awaitingTarget,
      avoid ? Math.round(avoid.left) : 'x',
      avoid ? Math.round(avoid.width) : 'x',
      avoid ? Math.round(avoid.top) : 'x',
      avoid ? Math.round(avoid.height) : 'x',
      this.done.size,
      this.skipped.size,
    ].join('|');

    if (!force && signature === this.signature) return;
    this.signature = signature;
    this.options.onState(state);
  }

  private clearTimer(): void {
    if (this.timer !== null) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
    if (this.onViewportChange) {
      document.removeEventListener('scroll', this.onViewportChange, { capture: true });
      window.removeEventListener('resize', this.onViewportChange);
      this.onViewportChange = null;
    }
  }
}
